import React, { useState, useEffect } from 'react';
import { apiClient } from '../../../../api/client';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT, BG_SURFACE } from '../designTokens';

interface ReservationGroup {
  id: string;
  name: string;
  active: boolean;
  locked: boolean;
  memberCount: number;
  members: Array<{
    id: string;
    fullName: string;
    seatLabel?: string | null;
  }>;
  placement?: {
    zone: string;
    tables: Array<{ tableNumber: number; tableName: string; zoneName: string }>;
  };
}

interface ReservationRow {
  name: string;
  guestCount: number;
  status: 'Seated' | 'Unseated' | 'Inactive';
  zones: string[];
}

const styles: Record<string, React.CSSProperties> = {
  container: {},
  title: {
    fontSize: '0.95rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: '0 0 0.75rem 0',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.82rem',
  },
  th: {
    textAlign: 'left' as const,
    padding: '0.5rem 0.6rem',
    fontWeight: 600,
    color: TEXT_DARK,
    borderBottom: `2px solid ${PRIMARY}`,
    backgroundColor: BG_SURFACE,
  },
  td: {
    padding: '0.45rem 0.6rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    color: '#333',
  },
  badge: {
    padding: '0.15rem 0.5rem',
    borderRadius: '10px',
    fontSize: '0.72rem',
    fontWeight: 600,
    display: 'inline-block',
  },
  empty: {
    padding: '1.5rem',
    textAlign: 'center' as const,
    color: TEXT_MUTED,
    fontSize: '0.85rem',
    fontStyle: 'italic' as const,
  },
  loading: {
    padding: '1.5rem',
    textAlign: 'center' as const,
    color: TEXT_MUTED,
    fontSize: '0.85rem',
  },
};

const STATUS_STYLES: Record<string, React.CSSProperties> = {
  Seated: { backgroundColor: '#dcfce7', color: '#166534' },
  Unseated: { backgroundColor: '#fef3c7', color: '#92400e' },
  Inactive: { backgroundColor: '#f3f4f6', color: '#6b7280' },
};

export function TableReservationsSection() {
  const [rows, setRows] = useState<ReservationRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchReservations() {
      try {
        const groups = await apiClient<ReservationGroup[]>('/batch-assignment/family-groups');
        // Filter to only RES- groups
        const resGroups = groups.filter((g) => g.name.toUpperCase().includes('RES-'));

        const mapped: ReservationRow[] = resGroups.map((g) => {
          // Determine status
          let status: 'Seated' | 'Unseated' | 'Inactive';
          if (!g.active) {
            status = 'Inactive';
          } else if (g.locked || (g.placement && g.placement.tables.length > 0)) {
            status = 'Seated';
          } else {
            // Check if any members have a seatLabel
            const anySeated = g.members.some((m) => m.seatLabel);
            status = anySeated ? 'Seated' : 'Unseated';
          }

          // Collect zones
          const zones: string[] = [];
          if (g.placement) {
            for (const t of g.placement.tables) {
              if (t.zoneName && !zones.includes(t.zoneName)) zones.push(t.zoneName);
            }
          }

          return {
            name: g.name,
            guestCount: g.memberCount,
            status,
            zones,
          };
        });

        setRows(mapped);
      } catch {
        setRows([]);
      } finally {
        setLoading(false);
      }
    }
    fetchReservations();
  }, []);

  if (loading) return <div style={styles.loading}>Loading table reservations...</div>;

  if (rows.length === 0) return <div style={styles.empty}>No table reservations (RES- groups) found.</div>;

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Table Reservations</h3>
      <table style={styles.table} aria-label="Table reservations">
        <thead>
          <tr>
            <th style={styles.th}>Name</th>
            <th style={styles.th}>Guests</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Zones</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.name}>
              <td style={{ ...styles.td, fontWeight: 600 }}>{row.name}</td>
              <td style={styles.td}>{row.guestCount}</td>
              <td style={styles.td}>
                <span style={{ ...styles.badge, ...STATUS_STYLES[row.status] }}>
                  {row.status}
                </span>
              </td>
              <td style={styles.td}>{row.zones.length > 0 ? row.zones.join(', ') : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
